import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import ReembolsoService from "../../services/ReembolsoService";
import CajaService from "../../services/CajaService";
import CajaPagosService from "../../services/CajaPagosService";
import TelefonosService from "../../services/TelefonosService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  Stack,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const SolicitudesNoLiquidadas = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [reembolsosList, setReembolsosList] = useState([]);
  const [estudiosMap, setEstudiosMap] = useState({});
  const [cajaMap, setCajaMap] = useState({});
  const [pagosPorSolicitudMap, setPagosPorSolicitudMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Usuario actual en localStorage
  const currentU = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("usuario")) || {};
    } catch (e) {
      return {};
    }
  }, []);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Checkboxes Adicionales
  const [mostrarDetalleEstudios, setMostrarDetalleEstudios] = useState(false);
  const [desgloseGanancias, setDesgloseGanancias] = useState(true);
  const [ocultarEmpresas, setOcultarEmpresas] = useState(false);

  useEffect(() => {
    document.title = "Solicitudes no Liquidadas";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }

    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }
    return null;
  };

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resCaja] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: { caja: [] } })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
          ? resSol.data
          : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
          ? resSuc.data
          : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.data)
          ? resCaja.data
          : [];

      // Mapear movimientos de caja por idSolicitud
      const cMap = {};
      listaCaja.forEach((c) => {
        if (c && c.idSolicitud) {
          cMap[c.idSolicitud] = c;
        }
      });
      setCajaMap(cMap);

      // Cargar pagos desglosados individuales por caja/solicitud
      const pMap = {};
      await Promise.all(
        listaSol.map(async (sol) => {
          const idCaja = sol.idCaja || cMap[sol.idSolicitud]?.idCaja;
          if (!idCaja) return;
          try {
            const resP = await CajaPagosService.getPagosPorCaja(idCaja);
            const arrP = Array.isArray(resP?.pagos)
              ? resP.pagos
              : Array.isArray(resP?.data?.pagos)
                ? resP.data.pagos
                : Array.isArray(resP)
                  ? resP
                  : [];
            pMap[sol.idSolicitud] = arrP;
          } catch (e) {
            pMap[sol.idSolicitud] = [];
          }
        })
      );
      setPagosPorSolicitudMap(pMap);

      // Obtención de Reembolsos de BD
      const rawReembolsos = [];

      try {
        const resGeneral = await ReembolsoService.getReembolsos();
        const payload = resGeneral?.data?.reembolsos ?? resGeneral?.data;
        if (Array.isArray(payload)) rawReembolsos.push(...payload);
      } catch (e) { }

      if (listaSuc.length > 0) {
        try {
          const reemProms = listaSuc.map((suc) =>
            ReembolsoService.getReembolsoActual(suc.idSucursal).catch(() => null)
          );
          const reemResps = await Promise.all(reemProms);

          reemResps.forEach((res, idx) => {
            const sucId = listaSuc[idx]?.idSucursal;
            const payload = res?.data?.reembolsos ?? res?.data;
            const arr = Array.isArray(payload) ? payload : payload ? [payload] : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push({
                  ...r,
                  idSucursal: r.idSucursal ?? r.sucursalId ?? sucId,
                });
              }
            });
          });
        } catch (e) { }
      }

      try {
        const resCortes = await CajaService.getCortes().catch(() => null);
        const cortesList = Array.isArray(resCortes?.data) ? resCortes.data : Array.isArray(resCortes) ? resCortes : [];
        if (cortesList.length > 0) {
          const cortesProms = cortesList.map((c) =>
            ReembolsoService.getReembolsoPorCorte(c.idSucursal || c.sucursalId, c.idCorte || c.idCorteReembolso).catch(() => null)
          );
          const cortesResps = await Promise.all(cortesProms);
          cortesResps.forEach((res) => {
            const payload = res?.reembolsos ?? res?.data?.reembolsos ?? res;
            const arr = Array.isArray(payload) ? payload : [];
            arr.forEach((r) => {
              if (r && typeof r === "object") {
                rawReembolsos.push(r);
              }
            });
          });
        }
      } catch (e) { }

      const seenKeys = new Set();
      const listaFinalReembolsos = [];

      rawReembolsos.forEach((r) => {
        const key = r.idReembolso
          ? `id:${r.idReembolso}`
          : `${r.idSucursal || r.sucursalId}-${r.fecha}-${r.monto || r.cantidad}-${r.motivo || ""}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          listaFinalReembolsos.push(r);
        }
      });

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
      setReembolsosList(listaFinalReembolsos);

      // Selección de sucursal por defecto = del usuario actual
      if (currentU.sucursal) {
        const foundSuc = listaSuc.find(
          (s) =>
            String(s.idSucursal) === String(currentU.sucursal) ||
            String(s.nombre).trim().toLowerCase() === String(currentU.sucursal).trim().toLowerCase()
        );
        if (foundSuc) {
          setSucursalSeleccionada(String(foundSuc.idSucursal));
        } else if (listaSuc.length > 0) {
          setSucursalSeleccionada(String(listaSuc[0].idSucursal));
        }
      } else if (listaSuc.length > 0) {
        setSucursalSeleccionada(String(listaSuc[0].idSucursal));
      }

      // Cargar estudios para cada solicitud
      const eMap = {};
      await Promise.all(
        listaSol.map(async (sol) => {
          if (sol.idSolicitud) {
            try {
              const resEst = await SolicitudesService.getEstudiosPorSolicitud(sol.idSolicitud);
              const estudiosArr = resEst?.data?.estudios ?? resEst?.data?.solicitudesEstudios ?? resEst?.data ?? [];
              if (Array.isArray(estudiosArr)) {
                eMap[sol.idSolicitud] = estudiosArr;
              }
            } catch (e) { }
          }
        })
      );
      setEstudiosMap(eMap);
    } catch (error) {
      console.error("Error cargando datos para Solicitudes no Liquidadas:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al consultar las solicitudes no liquidadas del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  const coincideSucursal = (item, sucId) => {
    if (!item || !sucId) return false;
    const idSucItem = String(item.idSucursal || item.sucursalId || item.sucursal || "");
    const nomSucItem = String(item.nombreSucursal || item.sucursal || "").trim().toLowerCase();

    const targetSuc = sucursales.find(
      (s) => String(s.idSucursal) === String(sucId) || String(s.nombre).trim().toLowerCase() === String(sucId).trim().toLowerCase()
    );

    if (targetSuc) {
      const targetNom = String(targetSuc.nombre).trim().toLowerCase();
      return idSucItem === String(targetSuc.idSucursal) || nomSucItem === targetNom;
    }
    return idSucItem === String(sucId) || nomSucItem === String(sucId).trim().toLowerCase();
  };

  const getDesglosePagoSolicitud = (sol) => {
    const cajaObj = cajaMap[sol.idSolicitud] || {};
    const pagos = pagosPorSolicitudMap[sol.idSolicitud] || [];

    const totalSol = Number(cajaObj.totalPagar ?? cajaObj.total ?? sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
    const descuento = Number(cajaObj.descuento ?? sol.descuentoTotal ?? sol.descuento ?? sol.descuentoMonto ?? 0);
    const iva = Number(cajaObj.iva ?? sol.iva ?? sol.montoIva ?? 0);

    // Subtotal de la caja (precio bruto de los estudios sin descuento ni IVA)
    const subtotal = Number(
      cajaObj.subtotal ??
      sol.subtotal ??
      sol.subtotalCaja ??
      sol.subtotalSolicitud ??
      (totalSol + descuento - iva)
    );

    let ef = 0,
      tj = 0,
      ch = 0,
      tr = 0;

    const detallesCobroList = [];

    if (pagos.length > 0) {
      pagos.forEach((p) => {
        const montoP = Number(p.monto || p.cantidad || p.importe || 0);
        const metodo = `${p.formaPago || p.metodoPago || p.tipoPago || ""}`.toLowerCase();
        const empNom = [p.nombreUsuario, p.apPaternoUsuario, p.apMaternoUsuario].filter(Boolean).join(" ").trim() || p.usuario || p.cobro || "Cajero";
        const fechaStr = p.fecha || p.fechaCobro || sol.fecha || getFechaHoy();
        const formaFmt = p.formaPago || p.metodoPago || "Efectivo";

        if (metodo.includes("tarjeta")) {
          tj += montoP;
        } else if (metodo.includes("cheque")) {
          ch += montoP;
        } else if (metodo.includes("transferencia") || metodo.includes("transf")) {
          tr += montoP;
        } else {
          ef += montoP;
        }

        detallesCobroList.push({
          cobroEmpleado: empNom,
          formaPago: formaFmt,
          monto: montoP,
          fechaStr: fechaStr,
        });
      });
    } else {
      let pagadoUnico = 0;
      if (cajaObj.pagado !== undefined && cajaObj.pagado !== null && cajaObj.pagado !== "") {
        pagadoUnico = Number(cajaObj.pagado);
      } else if (sol.pagado !== undefined && sol.pagado !== null && sol.pagado !== "") {
        pagadoUnico = Number(sol.pagado);
      } else if (sol.aCuenta !== undefined && sol.aCuenta !== null && sol.aCuenta !== "") {
        pagadoUnico = Number(sol.aCuenta);
      } else {
        const estatus = `${sol.estatus ?? cajaObj.estatus ?? ""}`.trim().toUpperCase();
        if (estatus === "P") pagadoUnico = totalSol;
      }

      if (pagadoUnico > 0) {
        const forma = `${cajaObj.formaPago || sol.formaPago || sol.metodoPago || ""}`.toLowerCase();
        const formaFmt = cajaObj.formaPago || sol.formaPago || "Efectivo";
        const empNom = cajaObj.usuarioCobro || sol.usuarioCobro || sol.empleado || sol.usuario || (currentU ? `${currentU.nombre || ""} ${currentU.apPaterno || ""}`.trim() : "") || "Cajero";
        const fechaStr = cajaObj.fechaCobro || sol.fechaCobro || sol.fecha || getFechaHoy();

        if (forma.includes("tarjeta")) tj = pagadoUnico;
        else if (forma.includes("cheque")) ch = pagadoUnico;
        else if (forma.includes("transferencia") || forma.includes("transf")) tr = pagadoUnico;
        else ef = pagadoUnico;

        detallesCobroList.push({
          cobroEmpleado: empNom,
          formaPago: formaFmt,
          monto: pagadoUnico,
          fechaStr: fechaStr,
        });
      }
    }

    const totalPagadoReal = ef + tj + ch + tr;

    let adeudo = 0;
    if (cajaObj.adeudo !== undefined && cajaObj.adeudo !== null && cajaObj.adeudo !== "") {
      adeudo = Number(cajaObj.adeudo);
    } else if (sol.saldoPendiente !== undefined && sol.saldoPendiente !== null && sol.saldoPendiente !== "") {
      adeudo = Number(sol.saldoPendiente);
    } else if (sol.adeudo !== undefined && sol.adeudo !== null && sol.adeudo !== "") {
      adeudo = Number(sol.adeudo);
    } else {
      adeudo = Math.max(0, totalSol - totalPagadoReal);
    }

    // Reembolsos asociados a esta solicitud
    const reemSol = reembolsosList.filter((r) => {
      const idReemSol = String(r.idSolicitud || r.codigo || "");
      const targetId = String(sol.idSolicitud || sol.codigo || "");
      return idReemSol && idReemSol === targetId;
    });
    const montoReembolso = reemSol.reduce((acc, r) => acc + Number(r.monto || r.cantidad || 0), 0);

    const empNomStr = detallesCobroList.map((d) => d.cobroEmpleado).join(", ") || "-";

    return {
      efectivo: ef,
      tarjeta: tj,
      cheque: ch,
      transferencia: tr,
      reembolso: montoReembolso,
      descuento: descuento,
      iva: iva,
      pagado: totalPagadoReal,
      adeudo: adeudo,
      total: subtotal,
      empleadoCobro: empNomStr,
      detallesCobroList,
    };
  };

  // Filtrado de solicitudes no liquidadas
  const solicitudesNoLiquidadas = useMemo(() => {
    const start = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const end = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);
    if (!start || !end) return [];

    return solicitudes.filter((sol) => {
      // Excluir canceladas
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      if (estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "")) {
        return false;
      }

      // Validar sucursal seleccionada (si hay sucursal seleccionada)
      if (sucursalSeleccionada && !coincideSucursal(sol, sucursalSeleccionada)) return false;

      // Validar rango de fechas
      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!dt) return false;
      if (dt.getTime() < start.getTime() || dt.getTime() > end.getTime()) return false;

      // Validar que sea NO LIQUIDADA (adeudo > 0.001)
      const d = getDesglosePagoSolicitud(sol);
      if (d.adeudo <= 0.001) return false;

      // Validar filtro Ocultar Solicitudes de Empresas
      const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
      if (ocultarEmpresas && tieneEmpresa) return false;

      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudes, sucursalSeleccionada, fechaDe, fechaA, ocultarEmpresas, reembolsosList, sucursales, cajaMap, pagosPorSolicitudMap]);

  // Totales acumulados de la tabla
  const totalesTabla = useMemo(() => {
    return solicitudesNoLiquidadas.reduce(
      (acc, sol) => {
        const d = getDesglosePagoSolicitud(sol);
        return {
          efectivo: acc.efectivo + d.efectivo,
          tarjeta: acc.tarjeta + d.tarjeta,
          cheque: acc.cheque + d.cheque,
          transferencia: acc.transferencia + d.transferencia,
          reembolso: acc.reembolso + d.reembolso,
          descuento: acc.descuento + d.descuento,
          iva: acc.iva + d.iva,
          pagado: acc.pagado + d.pagado,
          adeudo: acc.adeudo + d.adeudo,
          total: acc.total + d.total,
        };
      },
      {
        efectivo: 0,
        tarjeta: 0,
        cheque: 0,
        transferencia: 0,
        reembolso: 0,
        descuento: 0,
        iva: 0,
        pagado: 0,
        adeudo: 0,
        total: 0,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesNoLiquidadas, sucursalSeleccionada, fechaDe, fechaA, ocultarEmpresas, reembolsosList, sucursales, cajaMap, pagosPorSolicitudMap]);

  // Nombre de la sucursal seleccionada actualmente
  const sucursalNomActual = useMemo(() => {
    const found = sucursales.find(
      (s) =>
        String(s.idSucursal) === String(sucursalSeleccionada) ||
        String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
    );
    return found ? (found.nombre || "").toUpperCase() : "SUCURSAL";
  }, [sucursales, sucursalSeleccionada]);

  // Restablecer filtros
  const handleRestablecer = () => {
    setFechaDe(getFechaHoy());
    setFechaA(getFechaHoy());
    setMostrarDetalleEstudios(false);
    setDesgloseGanancias(true);
    setOcultarEmpresas(false);

    if (currentU.sucursal) {
      const foundSuc = sucursales.find(
        (s) =>
          String(s.idSucursal) === String(currentU.sucursal) ||
          String(s.nombre).trim().toLowerCase() === String(currentU.sucursal).trim().toLowerCase()
      );
      if (foundSuc) {
        setSucursalSeleccionada(String(foundSuc.idSucursal));
      } else if (sucursales.length > 0) {
        setSucursalSeleccionada(String(sucursales[0].idSucursal));
      }
    } else if (sucursales.length > 0) {
      setSucursalSeleccionada(String(sucursales[0].idSucursal));
    }
  };

  // Exportación a PDF Institucional
  const handleExportarPDF = async () => {
    if (solicitudesNoLiquidadas.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No hay solicitudes no liquidadas en el rango de fechas seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF();
        const startY = 12;

    let sucursalActualObj = sucursales.find(
      (s) =>
        String(s.idSucursal) === String(sucursalSeleccionada) ||
        String(s.nombre).trim().toLowerCase() === String(sucursalSeleccionada).trim().toLowerCase()
    ) || sucursales[0] || {};

    let sucursalTelefonosStr = "";
    if (sucursalActualObj.idSucursal) {
      try {
        const resTels = await TelefonosService.getTelefonosPorSucursal(sucursalActualObj.idSucursal);
        const listaTels = Array.isArray(resTels) ? resTels : resTels?.telefonos || [];
        sucursalTelefonosStr = listaTels.map((t) => t.telefono || t).join(", ");
      } catch (e) { }
    }

    const sucursalNomText = (sucursalActualObj.nombre || "SUCURSAL").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;
    const telText = `Tel. ${sucursalTelefonosStr || sucursalActualObj.telefono || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 28);
    } catch (e) { }

    // Rectángulo institucional "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Datos de la sucursal debajo del recuadro de informes
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });
    currY += 4;
    doc.text(telText, 196, currY, { align: "right" });

    // Título y Período
    currY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(24, 35, 79);
    doc.text(`SOLICITUDES NO LIQUIDADAS - SUCURSAL: ${sucursalNomText}`, 14, currY);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 4);

    let nextY = currY + 10;

    // Generar filas para el PDF respetando desgloseGanancias, descuento y estudios
    const filasPdf = [];

    solicitudesNoLiquidadas.forEach((sol) => {
      const d = getDesglosePagoSolicitud(sol);
      const pacienteNom = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
      const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
      const pacienteText = tieneEmpresa ? `${pacienteNom} (${sol.nombreConvenio.trim()})` : pacienteNom;

      if (desgloseGanancias) {
        filasPdf.push([
          sol.codigo || sol.idSolicitud,
          pacienteText,
          `$${d.efectivo.toFixed(2)}`,
          `$${d.tarjeta.toFixed(2)}`,
          `$${d.cheque.toFixed(2)}`,
          `$${d.transferencia.toFixed(2)}`,
          `$${d.reembolso.toFixed(2)}`,
          `$${d.descuento.toFixed(2)}`,
          `$${d.iva.toFixed(2)}`,
          `$${d.pagado.toFixed(2)}`,
          `$${d.adeudo.toFixed(2)}`,
          `$${d.total.toFixed(2)}`,
          d.detallesCobroList.length > 0
            ? d.detallesCobroList.map((det) => `${det.cobroEmpleado} (${det.formaPago}: $${det.monto.toFixed(2)})`).join("\n")
            : "-",
        ]);
      } else {
        filasPdf.push([sol.codigo || sol.idSolicitud, pacienteText]);
      }

      // Fila Motivo de Descuento si aplica
      const tieneMotivo = (d.descuento > 0 || (sol.motivoDescuento && sol.motivoDescuento.trim() !== ""));
      if (tieneMotivo && desgloseGanancias) {
        filasPdf.push([
          {
            content: `Motivo del descuento: ${sol.motivoDescuento || "Descuento en solicitud"}`,
            colSpan: 13,
            styles: { fillColor: [255, 248, 225], textColor: [183, 129, 3], fontStyle: "italic" },
          },
        ]);
      }

      // Fila Detalle de Estudios si aplica
      if (mostrarDetalleEstudios) {
        const estList = estudiosMap[sol.idSolicitud] || [];
        const padres = estList.filter((e) => !e.idEstudioPadre || Number(e.idEstudioPadre) === 0);
        const nombresEst = padres.map((e) => e.nombreEstudio || e.nombre || "Estudio").join(", ");
        const numCols = desgloseGanancias ? 13 : 2;

        filasPdf.push([
          {
            content: `Detalle de la solicitud: ${nombresEst || "Sin estudios registrados"}`,
            colSpan: numCols,
            styles: { fillColor: [238, 242, 246], textColor: [24, 35, 79], fontStyle: "bold" },
          },
        ]);
      }
    });

    // Fila Totales al final del PDF
    if (desgloseGanancias) {
      filasPdf.push([
        { content: "Totales:", colSpan: 2, styles: { fontStyle: "bold", halign: "right", fillColor: [238, 242, 246] } },
        `$${totalesTabla.efectivo.toFixed(2)}`,
        `$${totalesTabla.tarjeta.toFixed(2)}`,
        `$${totalesTabla.cheque.toFixed(2)}`,
        `$${totalesTabla.transferencia.toFixed(2)}`,
        `$${totalesTabla.reembolso.toFixed(2)}`,
        `$${totalesTabla.descuento.toFixed(2)}`,
        `$${totalesTabla.iva.toFixed(2)}`,
        `$${totalesTabla.pagado.toFixed(2)}`,
        `$${totalesTabla.adeudo.toFixed(2)}`,
        `$${totalesTabla.total.toFixed(2)}`,
        "",
      ]);
    }

    const headCols = desgloseGanancias
      ? [
        [
          { content: sucursalNomText, colSpan: 13, styles: { halign: "center", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" } },
        ],
        [
          "Código",
          "Paciente",
          "Pago efectivo",
          "Pago tarjeta",
          "Pago cheque",
          "Pago Transf",
          "Reembolsos",
          "Descuentos",
          "IVA",
          "Total pagado",
          "Por pagar",
          "Total solicitud",
          "Detalle",
        ],
      ]
      : [
        [
          { content: sucursalNomText, colSpan: 2, styles: { halign: "center", fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold" } },
        ],
        ["Código", "Paciente"],
      ];

    autoTable(doc, {
      startY: nextY,
      head: headCols,
      body: filasPdf,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 },
      styles: { fontSize: 6.5, cellPadding: 1.5, halign: "center" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" }, 1: { halign: "left" } },
    });

    // Pie de Página en cada hoja
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Solicitudes_No_Liquidadas_${sucursalNomText}_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Solicitudes No Liquidadas en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  SOLICITUDES NO LIQUIDADAS
                </Typography>
              </Box>

              {/* Filtros */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros de Búsqueda
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                          sx={{ width: "300px" }}
                        >
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal} value={String(suc.idSucursal)}>
                              {suc.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Adicionales Checkboxes */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Adicionales:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={mostrarDetalleEstudios}
                                onChange={(e) => setMostrarDetalleEstudios(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Mostrar detalle de los estudios realizados</Typography>}
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={desgloseGanancias}
                                onChange={(e) => setDesgloseGanancias(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Desglose de ganancias</Typography>}
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={ocultarEmpresas}
                                onChange={(e) => setOcultarEmpresas(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Ocultar solicitudes de empresas</Typography>}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            De:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            A:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: "180px" }}
                          />

                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleRestablecer}
                            startIcon={<RefreshIcon />}
                            sx={{ backgroundColor: "#18234f", color: "white", ml: 2 }}
                          >
                            Restablecer
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Tabla de Solicitudes No Liquidadas */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                  <Table
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      overflow: "hidden",
                      "& .MuiTableCell-root": { padding: "8px 10px", fontSize: "0.82rem" },
                    }}
                  >
                    <TableHead>
                      {/* Fila 1: Nombre de la Sucursal */}
                      <TableRow sx={{ backgroundColor: "#18234f" }}>
                        <TableCell
                          colSpan={desgloseGanancias ? 13 : 2}
                          align="center"
                          sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}
                        >
                          {sucursalNomActual}
                        </TableCell>
                      </TableRow>

                      {/* Fila 2: Cabeceras dinámicas */}
                      {desgloseGanancias ? (
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Código</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paciente</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago efectivo</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago tarjeta</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago cheque</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Pago Transferencia</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Reembolsos</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Descuentos</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>IVA</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total pagado</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Por pagar</TableCell>
                          <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Total solicitud</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Detalle</TableCell>
                        </TableRow>
                      ) : (
                        <TableRow sx={{ backgroundColor: "#006787" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold", width: "25%" }}>Código</TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>Paciente</TableCell>
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {solicitudesNoLiquidadas.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={desgloseGanancias ? 13 : 2}
                            align="center"
                            sx={{ color: "#888", py: 4 }}
                          >
                            No hay solicitudes no liquidadas para la sucursal y rango de fechas seleccionados.
                          </TableCell>
                        </TableRow>
                      ) : (
                        <>
                          {solicitudesNoLiquidadas.map((sol) => {
                            const d = getDesglosePagoSolicitud(sol);
                            const pacienteNom = `${sol.nombrePaciente || ""} ${sol.apPaternoPaciente || ""} ${sol.apMaternoPaciente || ""}`.trim();
                            const tieneEmpresa = sol.nombreConvenio && sol.nombreConvenio.trim() !== "";
                            const pacienteText = tieneEmpresa ? `${pacienteNom} (${sol.nombreConvenio.trim()})` : pacienteNom;

                            const tieneMotivo = (d.descuento > 0 || (sol.motivoDescuento && sol.motivoDescuento.trim() !== ""));
                            const estList = estudiosMap[sol.idSolicitud] || [];
                            const padres = estList.filter((e) => !e.idEstudioPadre || Number(e.idEstudioPadre) === 0);

                            return (
                              <React.Fragment key={sol.idSolicitud || sol.codigo}>
                                <TableRow sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                                  <TableCell sx={{ fontWeight: "bold" }}>{sol.codigo || sol.idSolicitud}</TableCell>
                                  <TableCell>{pacienteText}</TableCell>

                                  {desgloseGanancias && (
                                    <>
                                      <TableCell align="center">${d.efectivo.toFixed(2)}</TableCell>
                                      <TableCell align="center">${d.tarjeta.toFixed(2)}</TableCell>
                                      <TableCell align="center">${d.cheque.toFixed(2)}</TableCell>
                                      <TableCell align="center">${d.transferencia.toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ color: "#d32f2f" }}>${d.reembolso.toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ color: "#d32f2f" }}>${d.descuento.toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ color: "#1976d2" }}>${d.iva.toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32" }}>${d.pagado.toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${d.adeudo.toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ fontWeight: "bold" }}>${d.total.toFixed(2)}</TableCell>
                                      <TableCell>
                                        {d.detallesCobroList.length > 0 ? (
                                          d.detallesCobroList.map((det, detIdx) => (
                                            <Box
                                              key={detIdx}
                                              sx={{
                                                py: 0.5,
                                                borderBottom: detIdx < d.detallesCobroList.length - 1 ? "1px dashed #e0e0e0" : "none",
                                              }}
                                            >
                                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                {det.cobroEmpleado}
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: "#555" }}>
                                                [{det.formaPago}: ${det.monto.toFixed(2)} - {det.fechaStr}]
                                              </Typography>
                                            </Box>
                                          ))
                                        ) : (
                                          <Typography variant="body2" sx={{ color: "#888", fontStyle: "italic" }}>
                                            -
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>

                                {/* Fila Motivo de Descuento */}
                                {tieneMotivo && desgloseGanancias && (
                                  <TableRow sx={{ backgroundColor: "#fff8e1" }}>
                                    <TableCell colSpan={13} sx={{ fontStyle: "italic", fontSize: "0.85rem", color: "#b78103", pl: 4 }}>
                                      Motivo del descuento: {sol.motivoDescuento || "Descuento en solicitud"}
                                    </TableCell>
                                  </TableRow>
                                )}

                                {/* Fila Detalle de los Estudios Realizados */}
                                {mostrarDetalleEstudios && (
                                  <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
                                    <TableCell colSpan={desgloseGanancias ? 13 : 2} sx={{ pl: 4, py: 1.2 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#18234f", mb: 0.5 }}>
                                        Detalle de la solicitud
                                      </Typography>
                                      {padres.length === 0 ? (
                                        <Typography variant="caption" sx={{ color: "#777" }}>Sin estudios registrados.</Typography>
                                      ) : (
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                          {padres.map((est, idx) => (
                                            <Typography key={idx} variant="caption" sx={{ backgroundColor: "#ffffff", border: "1px solid #c4d1db", px: 1, py: 0.3, borderRadius: "4px", fontWeight: "medium" }}>
                                              {est.nombreEstudio || est.nombre || `Estudio #${est.idEstudio}`}
                                            </Typography>
                                          ))}
                                        </Stack>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}

                          {/* Fila Totales Generales */}
                          {desgloseGanancias && (
                            <TableRow sx={{ backgroundColor: "#eef2f6", borderTop: "2px solid #18234f" }}>
                              <TableCell colSpan={2} sx={{ fontWeight: "bold", textAlign: "right" }}>Totales:</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>${totalesTabla.efectivo.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>${totalesTabla.tarjeta.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>${totalesTabla.cheque.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>${totalesTabla.transferencia.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${totalesTabla.reembolso.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${totalesTabla.descuento.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>${totalesTabla.iva.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#2e7d32" }}>${totalesTabla.pagado.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#d32f2f" }}>${totalesTabla.adeudo.toFixed(2)}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>${totalesTabla.total.toFixed(2)}</TableCell>
                              <TableCell />
                            </TableRow>
                          )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón flotante lateral derecho para PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default SolicitudesNoLiquidadas;
